import { useCallback, useState } from 'react';
import useFetch, { ServiceParams, ServiceResponse } from '../hooks/useFetch';
import EndPoints from '../config/EndPoint';
import { Driver } from '../models/DriverModel';

const qs = (params?: Record<string, any>): string => {
  if (!params) return '';
  return Object.entries(params)
    .filter(([_, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
};

export type Events = Partial<{
  onSuccess: (r: ServiceResponse) => void;
  onError: (r: ServiceResponse) => void;
}>;
const useDriversService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();
  const [fetching, setFetching] = useState<Record<string, boolean>>({});

  const setFlag = useCallback((key: string, val: boolean) => {
    setFetching(prev => ({ ...prev, [key]: val }));
  }, []);
  const setFetch = useCallback((key: string, val: boolean) => {
    setFetching(prev => ({ ...prev, [key]: val }));
  }, []);

  const list = useCallback(
    (filters?: Record<string, any>, events?: any, signal?: AbortSignal) =>
      doGet({ 
        url: `${EndPoints.DRIVERS.LIST}${qs(filters) ? `?${qs(filters)}` : ""}`, 
        setFetching: v => setFlag('list', v), 
        signal, 
        ...events 
      } as ServiceParams),
    [doGet, setFlag]
  );

  const store = useCallback(
    (payload: Driver, events?: any) =>
      doPost({ url: EndPoints.DRIVERS.STORE, body: payload, setFetching: v => setFlag('store', v), ...events } as ServiceParams),
    [doPost, setFlag]
  );

  const update = useCallback(
    (id: number, payload: Driver, events?: any) =>
      doPut({ url: EndPoints.DRIVERS.UPDATE.replace(':id', String(id)), body: payload, setFetching: v => setFlag('update', v), ...events } as ServiceParams),
    [doPut, setFlag]
  );

  const remove = useCallback(
    (id: number, events?: any) =>
      doDelete({ url: EndPoints.DRIVERS.DELETE.replace(':id', String(id)), setFetching: v => setFlag('delete', v), ...events } as ServiceParams),
    [doDelete, setFlag]
  );

  
    const create = useCallback(
      (events: Events = {}) =>
        doGet({
          url: EndPoints.DRIVERS.CREATE,
          setFetching: v => setFetch('create', v),
          ...events,
        } as ServiceParams),
      [doGet, setFlag]
    );
  
    
    const edit = useCallback(
      (id: number, events: Events = {}) =>
        doGet({
          url: EndPoints.DRIVERS.EDIT.replace(':id', String(id)),
          setFetching: (v) => setFetch('edit', v),
          ...events,
        } as ServiceParams),
      [doGet, setFlag]
    );

    const byCompany = useCallback(
    (companyId: number, events?: Events, signal?: AbortSignal) =>
      doGet({ 
        url: EndPoints.DRIVERS.BY_COMPANY.replace(':id', String(companyId)), 
        setFetching: v => setFlag('byCompany', v), 
        signal,
        ...events 
      } as ServiceParams),
    [doGet, setFlag]
  );

  const updatePassExtensions = useCallback(
    (id: number, payload: Record<string, unknown>, events?: Events) =>
      doPut({
        url: EndPoints.DRIVERS.PASS_EXTENSIONS.replace(':id', String(id)),
        body: payload,
        setFetching: (v) => setFetch('updatePassExtensions', v),
        ...events,
      } as ServiceParams),
    [doPut, setFetch]
  );

  const getPassHistory = useCallback(
    (id: number, events?: Events, signal?: AbortSignal) =>
      doGet({
        url: EndPoints.DRIVERS.PASS_HISTORY.replace(':id', String(id)),
        setFetching: (v) => setFetch('passHistory', v),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );

  const releaseVehicle = useCallback(
    (id: number, comment?: string, events?: Events) =>
      doPut({
        url: EndPoints.DRIVERS.RELEASE_VEHICLE.replace(':id', String(id)),
        body: comment ? { comment } : {},
        setFetching: (v) => setFetch('releaseVehicle', v),
        ...events,
      } as ServiceParams),
    [doPut, setFetch]
  );

  return {
    fetching,
    list,
    store,
    update,
    remove,
    create,
    edit,
    byCompany,
    updatePassExtensions,
    getPassHistory,
    releaseVehicle,
  };
};

export default useDriversService;
